import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpToDottedLine,
  faEye,
  faEyeSlash,
  faTriangleExclamation,
} from '@fortawesome/sharp-solid-svg-icons';
import { featureCollection, point } from '@turf/helpers';
import { bbox, bboxPolygon } from '@turf/turf';
import { Feature, Polygon } from 'geojson';
import { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { v4 as uuid } from 'uuid';

import { ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import { GroupType } from '@/features/boundaries/deedPlotter/call';
import {
  DeedPlotterFeatureProperties,
  useDeedPlotterContext,
} from '@/features/boundaries/deedPlotter/provider';
import {
  convertDeedGroupToPolygon,
  convertDeedToAcres,
} from '@/features/boundaries/deedPlotter/utils/convert';
import type { CardProps } from '@/features/selectionsPanel/constants';
import { useGenericSelectionPanelFunctions } from '@/features/selectionsPanel/hooks/useGenericSelectionPanelFunctions';
import { formatAcres } from '@/features/selectionsPanel/util/determineSelectionsPanelAcres';
import { FeatureType } from '@/models/features';
import { BoundaryType } from '@/models/land';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { addFeature } from '@/redux/slices/featureSlice';
import type { RootState } from '@/redux/store';

import { Card } from '../card';
import { LineItem } from './lineItem';

export const DeedPlotterCard: FC<CardProps<GroupType & { idx: number }>> = ({
  feature: group,
}) => {
  const dispatch = useDispatch();
  const { mapInstance: map } = useMapboxContext();
  const { drawTool } = useSelector((state: RootState) => state.drawV2);
  const { handleHidingGroup, hiddenGroups, groupsGeometry } =
    useDeedPlotterContext();

  const bboxFeature = useMemo(() => {
    if (!group.startPoint) return undefined;
    if (
      group.startPoint &&
      groupsGeometry[group.idx]?.filter((call) => !!call).length === 0
    ) {
      return bboxPolygon(bbox(point(group.startPoint!, {})), {
        properties: {
          idx: group.idx,
          name: group.name,
        },
      });
    }

    return bboxPolygon(
      bbox(
        featureCollection([
          ...(groupsGeometry[group.idx]?.filter((call) => !!call) || []),
        ])
      ),
      {
        properties: {
          idx: group.idx,
          name: group.name,
        },
      }
    ) as Feature<Polygon, DeedPlotterFeatureProperties>;
  }, [group.idx, group.name, group.startPoint, groupsGeometry]);

  const { goToGeometryLocation } = useGenericSelectionPanelFunctions();

  const handleOnClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (bboxFeature) goToGeometryLocation(bboxFeature);
    },
    [goToGeometryLocation, bboxFeature]
  );

  const handleToggleVisible = useCallback(
    (e) => {
      e.stopPropagation();
      if (!map) return;

      handleHidingGroup(group.idx);
    },
    [group.idx, handleHidingGroup, map]
  );

  const featureIsHidden = useMemo(
    () => hiddenGroups.includes(group.idx),
    [group.idx, hiddenGroups]
  );

  const acres = useMemo(() => {
    return convertDeedToAcres(groupsGeometry[group.idx]);
  }, [group.idx, groupsGeometry]);

  const handleConvertToBoundary = useCallback(() => {
    if (!map) return;

    if (!acres) return;

    const actualPolygon = convertDeedGroupToPolygon(groupsGeometry[group.idx]);

    if (!actualPolygon) return;

    actualPolygon.properties = {
      ...actualPolygon.properties,
      acres,
      boundary_type: BoundaryType.DRAW,
      feature_ids: [],
      type: FeatureType.BOUNDARY,
    };
    actualPolygon.id = uuid();

    dispatch(addFeature({ feature: { ...actualPolygon } }));
    drawTool?.add(actualPolygon);
  }, [acres, dispatch, drawTool, group.idx, groupsGeometry, map]);

  return (
    <Card className="py-2" onClick={handleOnClick}>
      <div className={twMerge('flex h-5 items-center gap-2 py-1')}>
        <Typography
          className={twMerge(
            'overflow-hidden text-ellipsis whitespace-nowrap',
            !bboxFeature && 'opacity-50'
          )}
          title={group.name}
          variant="button2"
        >
          {group.name || `Group ${group.idx + 1}`}
        </Typography>
        {!bboxFeature && (
          <div
            data-tooltip-content="Group has no start point"
            data-tooltip-id={GLOBAL_TOOLTIP}
          >
            <FontAwesomeIcon
              className="text-orange-utility"
              icon={faTriangleExclamation}
            />
          </div>
        )}
        <div className="flex-grow" />
        {bboxFeature && acres && (
          <IconButton
            className={twMerge(
              'ml-auto h-5 w-5 p-0 text-gray-70',
              !bboxFeature && 'pointer-events-none opacity-50'
            )}
            variant={ButtonVariant.GHOST}
            onClick={handleConvertToBoundary}
          >
            <FontAwesomeIcon
              className="rotate-90"
              icon={faArrowUpToDottedLine}
            />
          </IconButton>
        )}
        <IconButton
          className={twMerge(
            'ml-auto h-5 w-5 p-0 text-gray-70',
            !bboxFeature && 'pointer-events-none opacity-50'
          )}
          variant={ButtonVariant.GHOST}
          onClick={handleToggleVisible}
        >
          <FontAwesomeIcon
            className="h-5 w-5 p-0"
            icon={!featureIsHidden ? faEye : faEyeSlash}
          />
        </IconButton>
      </div>
      {bboxFeature && acres && (
        <LineItem
          containerClassName="ml-0 mb-0 mt-2"
          label="Acres"
          value={formatAcres(acres)}
        />
      )}
    </Card>
  );
};
